import store from '@/store';

const currentPurchase = () => store.getState().purchase.toJS();

const identityId = (value) => value ?? null;

const fragmentIdentity = (fragment = {}) => ({
  id: identityId(fragment.id ?? fragment.trip?.id),
  connectionIds: Array.isArray(fragment.trips)
    ? fragment.trips.map(({ id, trip }) => identityId(id ?? trip?.id))
    : null,
});

const wayIdentity = (way) => {
  if (!way) return null;
  const fragments = Array.isArray(way.fragments) ? way.fragments.map(fragmentIdentity) : [];
  const primaryFragmentId = fragments[0]?.id || null;
  if (!fragments.length && !way.tripSlug) return null;
  return {
    fragments,
    tripSlug: way.tripSlug || primaryFragmentId,
  };
};

const currentConnectionIds = (way) =>
  Array.isArray(way?.trips) && way.trips.length
    ? way.trips.map(fragmentIdentity).map(({ id }) => id)
    : null;

const wayBindingIdentity = (way) => {
  if (!wayIdentity(way) && !currentConnectionIds(way)) return null;
  return {
    tripSlug: way?.tripSlug || null,
    connectionIds: currentConnectionIds(way),
  };
};

const entryWayIdentity = (way) => {
  const route = wayIdentity(way);
  return {
    route,
    primaryFragmentId: route?.fragments?.[0]?.id || null,
    knownTripSlug: way?.tripSlug || null,
    knownConnectionIds: currentConnectionIds(way),
  };
};

const connectionIdsMatch = (expected, actual) =>
  expected === null ||
  (Array.isArray(actual) &&
    expected.length === actual.length &&
    expected.every((id, index) => id === actual[index]));

/**
 * Captures the ordered itinerary identity used to distinguish Quick Purchase attempts.
 * @param {object} purchase Current purchase state.
 * @returns {string} Stable primitive itinerary key.
 */
export const getQuickPurchaseItineraryKey = (purchase = {}) =>
  JSON.stringify({
    departs: wayIdentity(purchase.departs),
    returns: wayIdentity(purchase.returns),
  });

/**
 * Captures current trip and connection bindings only to re-evaluate ownership.
 * It is not an orchestration start key: normal detail enrichment can change it.
 * @param {object} purchase - Current purchase state.
 * @returns {string} Primitive observation key for bound identity changes.
 */
export const getQuickPurchaseBindingObservationKey = (purchase = {}) =>
  JSON.stringify({
    departs: wayBindingIdentity(purchase.departs),
    returns: wayBindingIdentity(purchase.returns),
  });

/**
 * Creates a globally disjoint loading owner for one request under an initialization handle.
 * The caller owns attempt-key uniqueness among concurrent requests; no registry is retained here.
 * @param {object} initialization - Quick Purchase initialization handle.
 * @param {string|number} attemptKey - Caller-owned request/result identity.
 * @returns {string} Namespaced primitive reducer owner id.
 */
export const createQuickPurchaseAttemptLoadingOwnerId = (initialization, attemptKey) => {
  const normalizedAttemptKey = String(attemptKey ?? '');
  if (!initialization?.loadingOwnerId || !normalizedAttemptKey) {
    throw new Error('Quick Purchase attempt loading ownership requires initialization and key');
  }
  return `${initialization.loadingOwnerId}:attempt:${normalizedAttemptKey}`;
};

/**
 * Creates the single-generation Quick Purchase initialization authority.
 * Tests may inject a purchase reader; the application exports one singleton below.
 * @param {Function} [readPurchase] Current purchase reader.
 * @returns {object} Narrow initialization owner.
 */
export const createQuickPurchaseInitializationOwner = (readPurchase = currentPurchase) => {
  let generation = 0;
  let currentHandle = null;
  let expectedWays = null;
  let boundWays = null;

  const begin = ({ token, itineraryKey, purchase: purchaseInput }) => {
    generation += 1;
    const purchase = purchaseInput || readPurchase() || {};
    expectedWays = {
      departs: entryWayIdentity(purchase.departs),
      returns: entryWayIdentity(purchase.returns),
    };
    boundWays = {
      departs: {
        tripSlug: expectedWays.departs.knownTripSlug,
        connectionIds: expectedWays.departs.knownConnectionIds,
      },
      returns: {
        tripSlug: expectedWays.returns.knownTripSlug,
        connectionIds: expectedWays.returns.knownConnectionIds,
      },
    };
    const ownGeneration = generation;
    let handle;
    const isLatest = () => currentHandle === handle && generation === ownGeneration;
    const isCurrent = () => {
      if (!isLatest()) return false;

      const purchase = readPurchase() || {};
      if (purchase.token !== token || getQuickPurchaseItineraryKey(purchase) !== itineraryKey) {
        return false;
      }
      return ['departs', 'returns'].every((way) => {
        const bound = boundWays[way];
        const currentWay = purchase[way];
        if (bound.tripSlug && currentWay?.tripSlug !== bound.tripSlug) return false;
        return connectionIdsMatch(bound.connectionIds, currentConnectionIds(currentWay));
      });
    };

    /**
     * Describes whether this handle still owns the current booking identity.
     * `invalidated` means no newer generation replaced it, so owned loading may be released.
     * @returns {'current'|'invalidated'|'replaced'} Ownership status.
     */
    const getStatus = () => {
      if (!isLatest()) return 'replaced';
      return isCurrent() ? 'current' : 'invalidated';
    };

    handle = Object.freeze({
      generation: ownGeneration,
      loadingOwnerId: `quick-purchase:initialization:${ownGeneration}`,
      token,
      itineraryKey,
      isCurrent,
      getStatus,
      // This capability covers initialization-identity invalidation only. A result/request that
      // becomes stale while this handle remains current needs its own attempt-scoped capability.
      canReleaseLoading: () => getStatus() === 'invalidated',
    });
    currentHandle = handle;
    return handle;
  };

  const bindTripDetails = (handle, purchaseInput) => {
    if (!handle?.isCurrent()) return false;
    const purchase = purchaseInput || readPurchase() || {};

    const waysAreValid = ['departs', 'returns'].every((way) => {
      const currentWay = purchase[way];
      if (!currentWay) return true;
      const expected = expectedWays[way];
      const currentSlug = currentWay.tripSlug || null;
      const allowedSlug = expected.knownTripSlug || expected.primaryFragmentId;
      if (currentSlug && (!allowedSlug || currentSlug !== allowedSlug)) return false;

      if (!boundWays[way].tripSlug && currentSlug) boundWays[way].tripSlug = currentSlug;

      const connectionIds = currentConnectionIds(currentWay);
      if (
        connectionIds &&
        expected.knownConnectionIds &&
        !connectionIdsMatch(expected.knownConnectionIds, connectionIds)
      ) {
        return false;
      }
      if (!boundWays[way].connectionIds && connectionIds) {
        if (!currentSlug || currentSlug !== allowedSlug) return false;
        boundWays[way].connectionIds = [...connectionIds];
      }
      return true;
    });
    if (!waysAreValid) return false;

    return handle.isCurrent();
  };

  const invalidate = ({ token } = {}) => {
    if (token && currentHandle?.token !== token) return false;
    generation += 1;
    currentHandle = null;
    expectedWays = null;
    boundWays = null;
    return true;
  };

  return {
    begin,
    bindTripDetails,
    invalidate,
    getCurrent: () => currentHandle,
  };
};

const quickPurchaseInitialization = createQuickPurchaseInitializationOwner();

export default quickPurchaseInitialization;
